// Warns when a strategy uses a COMMUNITY library block that has since been unpublished / disabled /
// delisted. The block keeps working (its code was snapshot-copied into the strategy at insert time), so
// this is informational, not blocking. Curated built-ins can't be disabled, so we only check blocks whose
// provenance says librarySource === 'user'. RLS hides a disabled block from non-authors, so the signal is
// simply: a provenance id that is no longer in the public set.

import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useStrategy } from '../../contexts/StrategyContext';
import { useBlockLibraryGate } from '../../config/useBlockLibraryGate';
import { fetchAvailableLibraryIds } from '../../services/customBlockLibraryClient';
import { staleLibraryRefs } from '../../services/customBlockLibraryService';
import type { BlockData } from '../../types';

export const LibraryProvenanceWarnings: React.FC = () => {
  const gate = useBlockLibraryGate();
  const { blocks } = useStrategy();
  const [stale, setStale] = useState<Array<{ name: string; libraryBlockId: string }>>([]);

  // community-sourced library blocks referenced by this strategy
  const referenced = blocks
    .map((b) => b.data as BlockData)
    .filter((d) => d?.libraryBlockId && d.librarySource === 'user')
    .map((d) => ({ name: d.name ?? 'custom block', libraryBlockId: d.libraryBlockId as string }));
  const idKey = referenced.map((r) => r.libraryBlockId).sort().join(',');

  useEffect(() => {
    if (!gate.library.enabled || referenced.length === 0) { setStale([]); return; }
    let alive = true;
    fetchAvailableLibraryIds(referenced.map((r) => r.libraryBlockId)).then((available) => {
      if (alive) setStale(staleLibraryRefs(blocks, available));
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, gate.library.enabled]);

  if (!gate.library.enabled || stale.length === 0) return null;

  return (
    <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        <b>Heads up:</b> {stale.length === 1 ? 'a community block' : `${stale.length} community blocks`} used here {stale.length === 1 ? 'is' : 'are'} no longer available in the library (unpublished or removed). Your strategy still runs with the saved copy, but the block can't be re-added or updated.
        <span className="block mt-0.5 text-amber-700">{stale.map((s) => s.name).join(', ')}</span>
      </div>
    </div>
  );
};

export default LibraryProvenanceWarnings;
